import { useState } from "react";
import { Bell, Search, ShieldCheck } from "lucide-react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/auth/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";

const titleMap: Record<string, { title: string; subtitle: string }> = {
  "/platform": { title: "Platform Dashboard", subtitle: "Multi-tenant intelligence & operations" },
  "/platform/tenants": { title: "Tenants", subtitle: "Customer organizations across the platform" },
  "/platform/tenants/users": { title: "Tenants", subtitle: "Cross-tenant user directory" },
  "/platform/users": { title: "Tenants", subtitle: "Cross-tenant user directory" },
  "/platform/subscriptions": { title: "Subscriptions & Plans", subtitle: "Commercial operations & lifecycle" },
  "/platform/sites": { title: "Sites Overview", subtitle: "Cross-tenant site & geofence operations" },
  "/platform/ai-control": { title: "AI Assistant Control", subtitle: "Platform governance for AI capability" },
  "/platform/notifications": { title: "Global Notifications", subtitle: "Platform-wide broadcasts & alerts" },
  "/platform/audit": { title: "Audit Logs", subtitle: "Enterprise traceability & governance" },
  "/platform/settings": { title: "Platform Settings", subtitle: "Global product configuration" },
  "/platform/profile": { title: "Profile", subtitle: "Super administrator account" },
};

export function PlatformHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const base = "/" + location.pathname.split("/").slice(1, 3).join("/");
  const meta = titleMap[location.pathname] ?? titleMap[base] ?? { title: "GeoKerja Platform", subtitle: "Super Admin Console" };
  const [query, setQuery] = useState("");

  // Global search jumps to the Tenants directory (the platform's primary
  // entity) with the term applied; PlatformTenants reads `?q=` to seed it.
  const runSearch = () => {
    const q = query.trim();
    navigate(q ? `/platform/tenants?q=${encodeURIComponent(q)}` : "/platform/tenants");
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-card/80 px-4 backdrop-blur-md md:px-6">
      <SidebarTrigger className="text-muted-foreground hover:text-foreground" />

      {/* Title stays visible on mobile — hiding it left the user with no
          indication of which page they were on. The Super Admin badge and
          subtitle drop on narrow screens, where horizontal room is tight. */}
      <div className="min-w-0 flex-shrink">
        <div className="flex items-center gap-2">
          <h1 className="font-display truncate text-sm font-semibold leading-tight tracking-tight sm:text-base">{meta.title}</h1>
          <Badge variant="outline" className="hidden border-accent/40 bg-accent/10 text-accent text-[10px] font-semibold uppercase tracking-wider md:inline-flex">
            <ShieldCheck className="mr-1 h-3 w-3" /> Super Admin
          </Badge>
        </div>
        <p className="hidden truncate text-xs text-muted-foreground md:block">{meta.subtitle}</p>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <div className="relative hidden lg:block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
            placeholder="Search tenants…"
            className="h-9 w-80 pl-9 bg-secondary/50 border-transparent focus-visible:bg-card"
          />
          <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground">↵</kbd>
        </div>

        <Button size="icon" variant="ghost" onClick={() => navigate("/platform/notifications")}>
          <Bell className="h-4 w-4" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-1 flex items-center gap-2 rounded-full border bg-card p-1 pr-3 hover:bg-secondary transition-colors">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-accent to-primary text-[10px] font-semibold text-white">
                {user?.avatarInitials ?? "SA"}
              </div>
              <span className="hidden md:inline text-xs font-medium">{user?.name?.split(" ")[0] ?? "Admin"}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="text-sm font-medium">{user?.name}</div>
              <div className="text-xs font-normal text-muted-foreground">{user?.email}</div>
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-accent">Super Administrator</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/platform/profile")}>Profile</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/platform/settings")}>Platform Settings</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => { logout(); navigate("/login"); }}>
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
